/* What somebody typed and has not saved.
 *
 * A browser crash, a closed tab, a phone that ran out of battery halfway
 * through a long invoice — the work should still be there. Frappe's own desk
 * does this and people rely on it without noticing until it is missing.
 *
 * Per browser, and deliberately short-lived: a draft that outlives the
 * person's memory of making it is a trap, because they come back to a form
 * that silently disagrees with the saved document.
 */
const PREFIX = 'erp.draft.'
const MAX_AGE_MS = 24 * 60 * 60 * 1000

type Draft = { at: number; changes: Record<string, unknown>; seen?: string }

const key = (doctype: string, name: string) => `${PREFIX}${doctype}/${name}`

export function readDraft(doctype: string, name: string): Draft | null {
  try {
    const raw = localStorage.getItem(key(doctype, name))
    if (!raw) return null
    const draft = JSON.parse(raw) as Draft
    if (!draft?.changes || Date.now() - draft.at > MAX_AGE_MS) {
      forgetDraft(doctype, name)
      return null
    }
    return draft
  } catch {
    return null
  }
}

export function writeDraft(doctype: string, name: string, changes: Record<string, unknown>, seen?: string) {
  try {
    if (!changes || Object.keys(changes).length === 0) { forgetDraft(doctype, name); return }
    localStorage.setItem(key(doctype, name), JSON.stringify({ at: Date.now(), changes, seen }))
  } catch { /* private window, or full: the form still works, it just forgets */ }
}

export function forgetDraft(doctype: string, name: string) {
  try { localStorage.removeItem(key(doctype, name)) } catch { /* as above */ }
}

/** Drop anything older than a day, so storage does not fill with abandoned
 *  half-forms. Cheap enough to run when the app starts. */
export function sweepDrafts() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (!k?.startsWith(PREFIX)) continue
      const raw = localStorage.getItem(k)
      if (!raw) continue
      const draft = JSON.parse(raw) as Draft
      if (!draft?.at || Date.now() - draft.at > MAX_AGE_MS) localStorage.removeItem(k)
    }
  } catch { /* nothing depends on the sweep succeeding */ }
}
